import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Eye, ArrowLeft, ArrowRight, Printer, ChevronLeft, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface SurveyResponseViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surveyId: string;
  surveyTitle: string;
}

interface Question {
  id: string;
  question_text: string;
  question_type: string;
  sort_order: number;
  options: any;
}

interface ResponseRow {
  id: string;
  answers: Record<string, string | string[]>;
  created_at: string;
  respondent_id: string | null;
}

interface RespondentProfile {
  full_name: string | null;
  salutation: string | null;
}

const SurveyResponseViewer = ({ open, onOpenChange, surveyId, surveyTitle }: SurveyResponseViewerProps) => {
  const { roles } = useAuth();
  const isSuperadmin = roles.includes("superadmin");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [respondentProfile, setRespondentProfile] = useState<RespondentProfile | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && surveyId) loadData();
    if (!open) setSelectedIndex(null);
  }, [open, surveyId]);

  useEffect(() => {
    if (selectedIndex !== null && responses[selectedIndex]?.respondent_id) {
      loadRespondentProfile(responses[selectedIndex].respondent_id!);
    } else {
      setRespondentProfile(null);
    }
  }, [selectedIndex]);

  const loadData = async () => {
    setLoading(true);
    const [qRes, rRes] = await Promise.all([
      supabase.from("survey_questions").select("id, question_text, question_type, sort_order, options").eq("survey_id", surveyId).order("sort_order"),
      supabase.from("survey_responses").select("id, answers, created_at, respondent_id").eq("survey_id", surveyId).order("created_at", { ascending: false }),
    ]);
    setQuestions(qRes.data || []);
    setResponses((rRes.data || []).map(r => ({
      ...r,
      answers: (r.answers as Record<string, string | string[]>) || {},
    })));
    setLoading(false);
  };

  const loadRespondentProfile = async (userId: string) => {
    const { data } = await supabase.from("profiles").select("full_name, salutation").eq("user_id", userId).maybeSingle();
    setRespondentProfile(data || null);
  };

  const SESSIONS = [
    { key: "session_1", label: "8:00–10:00" },
    { key: "session_2", label: "10:30–12:30" },
    { key: "session_3", label: "1:30–3:30" },
  ];

  const renderModuleAvailability = (val: any) => {
    if (!val || typeof val !== "object") return "—";
    const modules = Object.entries(val) as [string, { day: string; sessions: string[] }[]][];
    if (modules.length === 0) return "—";

    return (
      <div className="space-y-3">
        {modules.map(([moduleName, days]) => (
          <div key={moduleName} className="border rounded-md overflow-hidden">
            <div className="bg-primary/10 px-3 py-1.5">
              <span className="text-xs font-semibold text-foreground">{moduleName}</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-3 py-1.5 font-medium text-muted-foreground w-[120px]">Day</th>
                  {SESSIONS.map(s => (
                    <th key={s.key} className="text-center px-2 py-1.5 font-medium text-muted-foreground">{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(days) ? days : []).map((entry, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-3 py-1.5 font-medium text-foreground">{entry.day}</td>
                    {SESSIONS.map(s => (
                      <td key={s.key} className="text-center px-2 py-1.5">
                        {entry.sessions?.includes(s.key) ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary/20 text-primary font-bold text-[10px]">✓</span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded text-muted-foreground/30">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  const formatAnswer = (val: string | string[] | undefined, questionType?: string) => {
    if (questionType === "module_availability") return renderModuleAvailability(val);
    if (!val) return "—";
    if (Array.isArray(val)) return val.join(", ");
    if (typeof val === "object") {
      try { return JSON.stringify(val, null, 2); } catch { return String(val); }
    }
    return val;
  };

  const deleteResponse = async (responseId: string) => {
    const { error } = await supabase.from("survey_responses").delete().eq("id", responseId);
    if (error) {
      toast.error("Failed to delete response: " + error.message);
      return;
    }
    toast.success("Response deleted");
    setResponses(prev => prev.filter(r => r.id !== responseId));
    if (selectedIndex !== null) {
      if (responses.length <= 1) setSelectedIndex(null);
      else if (selectedIndex >= responses.length - 1) setSelectedIndex(prev => (prev ?? 1) - 1);
    }
  };

  const currentResponse = selectedIndex !== null ? responses[selectedIndex] : null;

  const respondentDisplay = (() => {
    if (!respondentProfile) return null;
    const parts = [respondentProfile.salutation, respondentProfile.full_name].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  })();

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${surveyTitle} - Response</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20mm 15mm; color: #1a1a1a; font-size: 11pt; }
  .header { text-align: center; margin-bottom: 24px; border-bottom: 2px solid #1a365d; padding-bottom: 16px; }
  .header h1 { font-size: 16pt; color: #1a365d; margin-bottom: 4px; }
  .header .college { font-size: 10pt; color: #4a5568; }
  .meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #4a5568; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; }
  .question-card { margin-bottom: 16px; page-break-inside: avoid; }
  .question-label { font-weight: 600; font-size: 10pt; color: #2d3748; margin-bottom: 4px; }
  .question-number { color: #718096; font-weight: normal; }
  .answer { font-size: 11pt; color: #1a1a1a; padding: 8px 12px; background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 4px; min-height: 32px; }
  .footer { margin-top: 32px; text-align: center; font-size: 9pt; color: #a0aec0; border-top: 1px solid #e2e8f0; padding-top: 12px; }
</style></head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  // Summary view
  const renderSummary = () => (
    <>
      {responses.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No responses yet.</p>
      ) : (
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">#</TableHead>
                <TableHead className="text-xs">Date</TableHead>
                {questions.slice(0, 3).map(q => (
                  <TableHead key={q.id} className="text-xs min-w-[120px]">{q.question_text}</TableHead>
                ))}
                <TableHead className="text-xs">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {responses.map((r, i) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedIndex(i)}>
                  <TableCell className="text-xs">{i + 1}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {format(new Date(r.created_at), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  {questions.slice(0, 3).map(q => (
                    <TableCell key={q.id} className="text-xs max-w-[150px] truncate">
                      {formatAnswer(r.answers[q.id], q.question_type)}
                    </TableCell>
                  ))}
                  <TableCell className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setSelectedIndex(i); }}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {isSuperadmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={(e) => e.stopPropagation()}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Response</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently delete this response. This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => deleteResponse(r.id)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );

  // Individual response view
  const renderIndividual = () => {
    if (!currentResponse) return null;
    return (
      <div className="space-y-4">
        {/* Navigation bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIndex(null)}>
            <ChevronLeft className="h-4 w-4 mr-1" /> All Responses
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={selectedIndex === 0}
              onClick={() => setSelectedIndex(i => (i ?? 1) - 1)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {(selectedIndex ?? 0) + 1} of {responses.length}
            </span>
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={selectedIndex === responses.length - 1}
              onClick={() => setSelectedIndex(i => (i ?? 0) + 1)}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-1" /> Print / PDF
            </Button>
            {isSuperadmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive border-destructive/50 hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Response</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently delete this response. This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => currentResponse && deleteResponse(currentResponse.id)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Printable content */}
        <div ref={printRef}>
          <div className="header" style={{ textAlign: "center", marginBottom: 16, borderBottom: "2px solid hsl(var(--primary))", paddingBottom: 12 }}>
            <h1 style={{ fontSize: "1.1rem", fontWeight: 700, color: "hsl(var(--primary))" }}>{surveyTitle}</h1>
            <p className="college" style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>St. David KI College</p>
          </div>

          <div className="meta" style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "hsl(var(--muted-foreground))", borderBottom: "1px solid hsl(var(--border))", paddingBottom: 10, marginBottom: 16 }}>
            <span>
              {respondentDisplay ? (
                <><strong>Respondent:</strong> {respondentDisplay}</>
              ) : (
                <><strong>Respondent:</strong> Anonymous</>
              )}
            </span>
            <span>
              <strong>Submitted:</strong> {format(new Date(currentResponse.created_at), "dd MMMM yyyy, HH:mm")}
            </span>
          </div>

          <div className="space-y-3">
            {questions.map((q, i) => (
              <Card key={q.id} className="question-card">
                <CardContent className="py-3 px-4">
                  <p className="question-label text-xs font-semibold text-muted-foreground mb-1">
                    <span className="question-number">Q{i + 1}. </span>{q.question_text}
                  </p>
                  <div className={`answer text-sm p-2 rounded border min-h-[32px] ${q.question_type === "module_availability" ? "bg-transparent p-0 border-0" : "bg-muted/30"}`}>
                    {formatAnswer(currentResponse.answers[q.id], q.question_type)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {selectedIndex !== null ? `Response ${selectedIndex + 1}` : `Responses: ${surveyTitle}`}
          </DialogTitle>
          <DialogDescription>
            {selectedIndex !== null
              ? `Viewing individual response for "${surveyTitle}"`
              : `${responses.length} response${responses.length !== 1 ? "s" : ""} collected`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Loading...</p>
        ) : selectedIndex !== null ? renderIndividual() : renderSummary()}
      </DialogContent>
    </Dialog>
  );
};

export default SurveyResponseViewer;
